import { validateProduct } from 'lib/validators/product';
import { IDeal } from 'store/deals/types';
import errorMessages from 'lib/validators/deal.errors';

/**
 * Takes a deal object and validates the basic fields
 *
 * @param deal A deal object
 * @returns Returns an array of errors, or undefined if none are present
 */

const maxName = 30;
const maxProducts = 10;

export function validateDeal(deal: IDeal): Error[] | undefined {
  const errors: Error[] = [];
  const { name, products } = deal;
  // Check deal name length (0 char min)
  if (!name || name.length === 0 || name.length > maxName) {
    errors.push(new Error(errorMessages.invalidName));
  }

  // Validate products (if applicable)
  if (products?.length) {
    // No more than 10 products
    if (products.length > maxProducts) {
      errors.push(new Error(errorMessages.invalidProducts));
    }

    // Validate products
    products.forEach(product => {
      const e = validateProduct(product);
      if (e) {
        e.forEach(error => errors.push(error));
      }
    });
  }

  return errors.length ? errors : undefined;
}

/**
 * Takes a deal object and validates whether it can be published or not (more strict)
 *
 * @param deal A deal object
 * @returns Returns an array of errors, or undefined if none are present
 */
export function validatePublishedDeal(deal: IDeal): Error[] | undefined {
  let errors: Error[] = [];

  const { productLine, products, region } = deal;

  // Validate basic fields
  const basicDealErrors = validateDeal(deal);
  if (basicDealErrors) {
    errors = [...basicDealErrors];
  }

  // Region
  if (!region) {
    errors.push(new Error(errorMessages.invalidRegion));
  }

  // Product Line
  if (!productLine) {
    errors.push(new Error(errorMessages.invalidProductLine));
  }

  // At least one product
  if (!products || products.length === 0) {
    errors.push(new Error(errorMessages.productRequired));
  }

  // No more than 10 products
  if (products && products.length > maxProducts) {
    errors.push(new Error(errorMessages.productLimitExceeded));
  }

  return errors.length ? errors : undefined;
}

// Must be 1-30 char
export const validateName = (name: string): string | null => {
  if (!name || name.length > maxName) {
    return errorMessages.invalidName;
  }
  return null;
};

// Must be selected
export const validateStatus = (status: string): string | null => {
  if (!status) {
    return errorMessages.dealStatusNotSelected;
  }
  return null;
};
